import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import type { LegacyFile, ProviderDetail } from '../../../../../js/api';
import { ProvidersService } from '../../../../../js/api';
import { Badge } from '../../../components/badge';
import { Button } from '../../../components/button';
import { useHasPermission } from '../../../hooks/useHasPermission';
import { PROVIDER_UPDATE_PERMISSION } from './CredentialEntriesTab';
import { LegacyFileConvertDialog, useSetLegacyFileIgnored } from './LegacyFileConvertDialog';
import { formatProviderDate, labelFor, ProviderDrawerMaps, textOrDash } from './providerDisplay';
import { legacyFilesQueryKey } from './queryKeys';

export const LEGACY_UPLOADS_SECTION_ID = 'provider-legacy-uploads';

/** One legacy upload: its action (convert, or restore when ignored), what it is, the download. */
function LegacyUploadRow({
  file,
  sourceLabel,
  canUpdate,
  isRestoring,
  onConvert,
  onRestore,
}: {
  file: LegacyFile;
  sourceLabel: string;
  canUpdate: boolean;
  isRestoring: boolean;
  onConvert: () => void;
  onRestore: () => void;
}) {
  const ignored = file.is_ignored;
  return (
    <tr
      className={`tw:border-t tw:border-zinc-200 tw:align-top ${ignored ? 'tw:text-zinc-500' : ''}`}
      data-testid={`provider-legacy-upload-${file.source_id}`}
    >
      {canUpdate && (
        <td className="tw:py-1 tw:pr-2 tw:whitespace-nowrap">
          {ignored ? (
            <Button
              color="zinc-outline"
              small
              onClick={onRestore}
              disabled={isRestoring}
              title="Restore to the legacy uploads"
              aria-label="Restore to the legacy uploads"
              data-testid={`provider-legacy-upload-restore-${file.source_id}`}
            >
              <i className="fas fa-fw fa-rotate-left" aria-hidden="true" />
            </Button>
          ) : (
            <Button
              color="brand-green-outline"
              small
              onClick={onConvert}
              title="Convert into a document"
              aria-label="Convert into a document"
              data-testid={`provider-legacy-upload-convert-${file.source_id}`}
            >
              <i className="fas fa-fw fa-file-import" aria-hidden="true" />
            </Button>
          )}
        </td>
      )}
      <td className="tw:py-1.5 tw:pr-3 tw:whitespace-nowrap">
        {sourceLabel}
        {ignored && (
          <Badge color="zinc" className="tw:ml-2">
            Ignored
          </Badge>
        )}
      </td>
      <td className="tw:py-1.5 tw:pr-3">{textOrDash(file.label)}</td>
      <td className="tw:py-1.5 tw:pr-3">
        {/* legacy names are often UUID-ish and long: show at least ~30 chars, then
            an ellipsis (the table scrolls sideways); the full name is on hover */}
        <a
          href={file.download_url}
          target="_blank"
          rel="noopener noreferrer"
          title={file.file_name}
          className="tw:block tw:min-w-[30ch] tw:max-w-[28rem] tw:truncate tw:text-brand-green-0 tw:hover:underline"
          data-testid={`provider-legacy-upload-link-${file.source_id}`}
        >
          {file.file_name}
        </a>
      </td>
      <td className="tw:py-1.5 tw:pr-3 tw:whitespace-nowrap">
        {formatProviderDate(file.issue_date)}
      </td>
      <td className="tw:py-1.5 tw:pr-3 tw:whitespace-nowrap">
        {formatProviderDate(file.expiration_date)}
      </td>
      <td className="tw:py-1.5 tw:whitespace-nowrap">{formatProviderDate(file.uploaded_at)}</td>
    </tr>
  );
}

/**
 * The clinician's uploads that predate the documents framework (profile uploads by
 * type, "other documents", v1 reference letters, the legacy files behind credential
 * entries), listed in one place at the bottom of the Documents tab until they are
 * converted. Each row downloads, and staff with the update code convert it into a
 * Document of a type they pick (`LegacyFileConvertDialog`) or ignore it there; converted
 * rows drop off for good, ignored ones wait behind "Show ignored" where they can be
 * restored. Renders nothing once there is neither.
 */
export function LegacyUploadsSection({
  provider,
  maps,
  onConverted,
}: {
  provider: ProviderDetail;
  maps: ProviderDrawerMaps;
  /** The host refetches its documents queries (count, list, credential rows). */
  onConverted: () => void;
}) {
  const providerId = provider.id;
  const canUpdate = useHasPermission(PROVIDER_UPDATE_PERMISSION);
  const [converting, setConverting] = useState<LegacyFile | null>(null);
  const [showIgnored, setShowIgnored] = useState(false);
  const restoreMutation = useSetLegacyFileIgnored(providerId);

  const { data: files, isLoading } = useQuery<LegacyFile[]>({
    queryKey: legacyFilesQueryKey(providerId),
    queryFn: () =>
      ProvidersService.providersLegacyFilesList({ id: providerId }).then((data) =>
        Array.isArray(data) ? data : ((data as { results?: LegacyFile[] })?.results ?? [])
      ),
    enabled: !!providerId,
  });

  if (isLoading || !files || files.length === 0) return null;

  const pending = files.filter((file) => !file.is_ignored);
  const ignored = files.filter((file) => file.is_ignored);
  const rows = showIgnored ? [...pending, ...ignored] : pending;
  const hasPending = pending.length > 0;

  const sourceLabel = (file: LegacyFile) =>
    file.source === 'PROVIDER_FILE' && file.file_type
      ? labelFor(file.file_type, maps.ProviderFileType)
      : labelFor(file.source, maps.LegacyFileSource);

  return (
    <section
      id={LEGACY_UPLOADS_SECTION_ID}
      className="tw:mt-6 tw:flex tw:flex-col tw:gap-2"
      data-testid="provider-legacy-uploads"
    >
      <div
        className={`tw:flex tw:items-baseline tw:gap-2 tw:px-2 tw:py-1 tw:rounded-md ${
          hasPending ? 'tw:bg-amber-50' : 'tw:bg-zinc-50'
        }`}
      >
        {hasPending && (
          <i
            className="fas fa-triangle-exclamation tw:text-amber-700 tw:text-xs"
            aria-hidden="true"
          />
        )}
        {/* Bootstrap's reboot styles bare headings; force the size and margins. */}
        <h4
          className={`tw:!text-sm tw:!font-semibold tw:!leading-5 tw:!m-0 ${
            hasPending ? 'tw:text-amber-800' : 'tw:text-zinc-600'
          }`}
        >
          Legacy uploads
        </h4>
        <span className={`tw:text-xs ${hasPending ? 'tw:text-amber-700' : 'tw:text-zinc-500'}`}>
          {hasPending
            ? `(${pending.length}) not yet in Documents${
                canUpdate ? ' — convert each one into a document of the right type' : ''
              }`
            : 'nothing left to convert'}
        </span>
        {ignored.length > 0 && (
          <button
            type="button"
            className="tw:ml-auto tw:text-xs tw:text-zinc-600 tw:underline tw:hover:text-zinc-800"
            onClick={() => setShowIgnored((shown) => !shown)}
            aria-expanded={showIgnored}
            data-testid="provider-legacy-uploads-toggle-ignored"
          >
            {showIgnored ? 'Hide' : 'Show'} ignored ({ignored.length})
          </button>
        )}
      </div>
      {rows.length > 0 && (
        <div className="tw:overflow-x-auto">
          <table className="tw:w-full tw:text-sm tw:!mb-0">
            <thead>
              <tr className="tw:text-left tw:text-xs tw:text-zinc-500">
                {canUpdate && <th className="tw:py-1 tw:pr-2 tw:font-medium" />}
                <th className="tw:py-1 tw:pr-3 tw:font-medium">Source</th>
                <th className="tw:py-1 tw:pr-3 tw:font-medium">Label</th>
                <th className="tw:py-1 tw:pr-3 tw:font-medium">File</th>
                <th className="tw:py-1 tw:pr-3 tw:font-medium">Issued</th>
                <th className="tw:py-1 tw:pr-3 tw:font-medium">Expires</th>
                <th className="tw:py-1 tw:font-medium">Uploaded</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((file) => (
                <LegacyUploadRow
                  key={file.source_id}
                  file={file}
                  sourceLabel={sourceLabel(file)}
                  canUpdate={canUpdate}
                  isRestoring={restoreMutation.isPending}
                  onConvert={() => setConverting(file)}
                  onRestore={() => restoreMutation.mutate({ file, ignored: false })}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
      {converting && (
        <LegacyFileConvertDialog
          key={converting.source_id}
          open
          onClose={() => setConverting(null)}
          providerId={providerId}
          file={converting}
          sourceLabel={sourceLabel(converting)}
          onConverted={onConverted}
        />
      )}
    </section>
  );
}
